import type { ConnectionStatus } from "@/chat/connection"
import {
  PROTOCOL_VERSION,
  type Attachment,
  type Message,
  type Reaction,
  type ReplySnapshot,
  type RoomSummary,
  type ServerFrame,
  type User,
  type VoicePeer,
} from "@/chat/protocol"

/**
 * All live chat state, driven by frames from the socket.
 *
 * The reducer is the only place server frames are applied, so the rules that
 * matter — messages are unique by id, ordered by `(at, id)`, and an optimistic
 * row disappears exactly when its broadcast copy lands — are enforced in one
 * place rather than spread across screens.
 */

/**
 * A message typed locally that the server has not yet echoed back. Its
 * attachments are already uploaded; only the `send` is outstanding.
 */
export type PendingMessage = {
  nonce: string
  text: string
  attachments: Attachment[]
  /** The quoted message, shown above the pending row as it will be once sent. */
  replyTo: ReplySnapshot | null
  at: number
}

type TypingEntry = { user: User; at: number }

/** How long a typing notice stays on screen after the last keystroke frame. */
export const TYPING_TTL_MS = 4000

export type State = {
  status: ConnectionStatus
  /** Set once the server has answered `hello`. */
  self: User | null
  /** True when the server speaks a protocol this build does not. */
  protocolMismatch: boolean
  /**
   * True once the server has refused the session token. The token is dead —
   * expired or revoked — and the only way forward is a fresh sign-in.
   */
  sessionExpired: boolean
  rooms: RoomSummary[]
  messages: Record<string, Message[]>
  /** Whether the oldest loaded message is the first in the room. */
  atStart: Record<string, boolean>
  members: Record<string, User[]>
  typing: Record<string, TypingEntry[]>
  pending: Record<string, PendingMessage[]>
  /** Rooms with a history request in flight. */
  loading: Record<string, boolean>
  /** Who is in each room's voice chat; a room without one has no entry. */
  voice: Record<string, VoicePeer[]>
  maxMessageLength: number
  maxUploadBytes: number
  maxVideoUploadBytes: number
  maxAttachments: number
  notice: string | null
}

export const initialState: State = {
  status: "connecting",
  self: null,
  protocolMismatch: false,
  sessionExpired: false,
  rooms: [],
  messages: {},
  atStart: {},
  members: {},
  typing: {},
  pending: {},
  loading: {},
  voice: {},
  maxMessageLength: 2000,
  maxUploadBytes: 10 * 1024 * 1024,
  maxVideoUploadBytes: 100 * 1024 * 1024,
  maxAttachments: 4,
  notice: null,
}

export type Action =
  | { type: "status"; status: ConnectionStatus }
  | { type: "frame"; frame: ServerFrame }
  | { type: "queued"; roomId: string; message: PendingMessage }
  | { type: "loading"; roomId: string }
  | { type: "sweepTyping"; now: number }
  | { type: "dismissNotice" }
  | { type: "reset" }

/** Chronological, with the id as a stable tie-break for same-millisecond sends. */
function compareMessages(a: Message, b: Message): number {
  return a.at === b.at ? a.id.localeCompare(b.id) : a.at - b.at
}

/**
 * Unions two message lists by id. History pages, live broadcasts and reconnect
 * replays all overlap, so every path into the message list goes through here
 * rather than assuming it holds something new.
 */
export function mergeMessages(
  existing: Message[],
  incoming: Message[]
): Message[] {
  if (incoming.length === 0) return existing

  const byId = new Map(existing.map((m) => [m.id, m]))
  let changed = false

  for (const message of incoming) {
    if (!byId.has(message.id)) changed = true
    byId.set(message.id, withDefaults(message))
  }

  if (!changed && incoming.length === 0) return existing

  return [...byId.values()].sort(compareMessages)
}

/**
 * The reaction list with `userId`'s `emoji` added or removed — the same shape
 * the server will answer with, so an optimistic update and the frame that
 * settles it look alike. An emoji nobody holds any more is dropped.
 */
export function toggleReaction(
  reactions: Reaction[],
  emoji: string,
  userId: string,
  on: boolean
): Reaction[] {
  const index = reactions.findIndex((r) => r.emoji === emoji)

  if (index === -1) {
    return on ? [...reactions, { emoji, userIds: [userId] }] : reactions
  }

  const current = reactions[index]
  const has = current.userIds.includes(userId)
  if (has === on) return reactions

  const userIds = on
    ? [...current.userIds, userId]
    : current.userIds.filter((id) => id !== userId)

  const next = [...reactions]
  if (userIds.length === 0) next.splice(index, 1)
  else next[index] = { emoji, userIds }
  return next
}

/**
 * Fills the fields a server on an older protocol leaves out. The mismatch is
 * reported separately; this keeps the thread rendering meanwhile instead of
 * crashing on `undefined.map`.
 */
function withDefaults(message: Message): Message {
  const partial = message as Partial<Message>
  return partial.reactions &&
    partial.attachments &&
    partial.replyTo !== undefined
    ? message
    : {
        ...message,
        attachments: partial.attachments ?? [],
        reactions: partial.reactions ?? [],
        replyTo: partial.replyTo ?? null,
      }
}

export function reducer(state: State, action: Action): State {
  switch (action.type) {
    case "status":
      return { ...state, status: action.status }

    case "dismissNotice":
      return { ...state, notice: null }

    case "reset":
      return { ...initialState, status: state.status }

    case "loading":
      return {
        ...state,
        loading: { ...state.loading, [action.roomId]: true },
      }

    case "queued": {
      const queue = state.pending[action.roomId] ?? []
      return {
        ...state,
        pending: {
          ...state.pending,
          [action.roomId]: [...queue, action.message],
        },
      }
    }

    case "sweepTyping": {
      let changed = false
      const typing: Record<string, TypingEntry[]> = {}

      for (const [roomId, entries] of Object.entries(state.typing)) {
        const live = entries.filter((e) => action.now - e.at < TYPING_TTL_MS)
        if (live.length !== entries.length) changed = true
        if (live.length > 0) typing[roomId] = live
      }

      return changed ? { ...state, typing } : state
    }

    case "frame":
      return applyFrame(state, action.frame)
  }
}

/** A copy of `record` without `key`. */
function without<T>(record: Record<string, T>, key: string): Record<string, T> {
  if (!(key in record)) return record
  const copy = { ...record }
  delete copy[key]
  return copy
}

function upsertRoom(rooms: RoomSummary[], room: RoomSummary): RoomSummary[] {
  const index = rooms.findIndex((r) => r.id === room.id)
  if (index === -1) return [...rooms, room]

  const next = [...rooms]
  next[index] = room
  return next
}

function applyFrame(state: State, frame: ServerFrame): State {
  switch (frame.type) {
    case "welcome":
      return {
        ...state,
        self: frame.self,
        rooms: frame.rooms,
        protocolMismatch: frame.protocol !== PROTOCOL_VERSION,
        maxMessageLength: frame.limits.maxMessageLength,
        maxUploadBytes: frame.limits.maxUploadBytes,
        maxVideoUploadBytes: frame.limits.maxVideoUploadBytes,
        maxAttachments: frame.limits.maxAttachments,
        // The server follows `welcome` with the calls going on right now.
        voice: {},
        notice: null,
      }

    case "history": {
      const existing = state.messages[frame.roomId] ?? []
      return {
        ...state,
        messages: {
          ...state.messages,
          [frame.roomId]: mergeMessages(existing, frame.messages),
        },
        // Only a page that reached the beginning settles `atStart`; a later
        // page must not un-set it.
        atStart: {
          ...state.atStart,
          [frame.roomId]:
            frame.atStart || (state.atStart[frame.roomId] ?? false),
        },
        loading: { ...state.loading, [frame.roomId]: false },
      }
    }

    case "message": {
      const { message } = frame
      const roomId = message.roomId
      const existing = state.messages[roomId] ?? []

      // The sender's own copy carries the nonce that retires the optimistic row.
      const pending = message.nonce
        ? (state.pending[roomId] ?? []).filter((p) => p.nonce !== message.nonce)
        : state.pending[roomId]

      return {
        ...state,
        messages: {
          ...state.messages,
          [roomId]: mergeMessages(existing, [message]),
        },
        pending: pending
          ? { ...state.pending, [roomId]: pending }
          : state.pending,
      }
    }

    case "presence":
      return {
        ...state,
        members: { ...state.members, [frame.roomId]: frame.members },
      }

    case "reactions": {
      // The list replaces the message's own; a message not loaded here (older
      // than the history on hand) has nothing to update.
      const existing = state.messages[frame.roomId]
      const index = existing?.findIndex((m) => m.id === frame.messageId) ?? -1
      if (!existing || index === -1) return state

      const next = [...existing]
      next[index] = { ...existing[index], reactions: frame.reactions }
      return { ...state, messages: { ...state.messages, [frame.roomId]: next } }
    }

    case "typing": {
      // Your own typing never comes back from the server, so anything here is
      // someone else. Refresh the entry rather than stacking duplicates.
      const entries = (state.typing[frame.roomId] ?? []).filter(
        (e) => e.user.id !== frame.user.id
      )

      return {
        ...state,
        typing: {
          ...state.typing,
          [frame.roomId]: [...entries, { user: frame.user, at: Date.now() }],
        },
      }
    }

    // `roomCreated` is an upsert too: the requester of a direct room gets it
    // even when the room already existed on their side.
    case "roomCreated":
    case "roomUpdated":
    case "direct":
      return { ...state, rooms: upsertRoom(state.rooms, frame.room) }

    // Leaving a room forgets everything about it; the history would be stale
    // and inaccessible anyway.
    case "roomRemoved":
      return {
        ...state,
        rooms: state.rooms.filter((r) => r.id !== frame.roomId),
        messages: without(state.messages, frame.roomId),
        members: without(state.members, frame.roomId),
        typing: without(state.typing, frame.roomId),
        pending: without(state.pending, frame.roomId),
        voice: without(state.voice, frame.roomId),
      }

    case "voiceState":
      return {
        ...state,
        voice:
          frame.peers.length > 0
            ? { ...state.voice, [frame.roomId]: frame.peers }
            : without(state.voice, frame.roomId),
      }

    case "error":
      if (frame.code === "unauthorized") {
        return { ...state, sessionExpired: true, notice: frame.message }
      }
      return { ...state, notice: frame.message }

    case "system": {
      // The server does not emit these today; ignoring them keeps the client
      // forward-compatible rather than crashing on an unknown shape.
      return state
    }

    default:
      return state
  }
}
